import React, { useEffect, useMemo } from "react";
import { useAppDispatch } from "../../state/hooks";
import { setScreenFormData } from "../../state/slices/formsSlice";
import { store } from "../../state/store";
import { apiService } from "../../services/api";
import HrmStatCards from "./HrmStatCards";

type CardConfig = {
  name?: string;
  bindValue?: string;
  icon?: React.ReactNode;
  defaultValue?: number;
  valueFormat?: string;
  color?: string;
  description?: string;
};

interface InfoCardsWithStatsProps {
  screenId: string;
  infoCardsConfig: {
    apiname?: string;
    cards?: CardConfig[];
  };
  formData: Record<string, any>;
}

function normalizeHrmsInfoCardsPath(apiname: string | undefined): string | null {
  if (!apiname || typeof apiname !== "string") return null;
  const trimmed = apiname.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("/pabws/")) return trimmed;
  if (trimmed.startsWith("/g360/")) return `/pabws${trimmed.slice("/g360".length)}`;
  if (trimmed.startsWith("/hrms/")) return `/pabws${trimmed}`;
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

/**
 * HRMS stat cards: GET `apiname` (normalized to /pabws/...) and merge bindValue fields into screen formData.
 */
const InfoCardsWithStats: React.FC<InfoCardsWithStatsProps> = ({
  screenId,
  infoCardsConfig,
  formData,
}) => {
  const dispatch = useAppDispatch();
  const apiname = infoCardsConfig.apiname;
  const cards = infoCardsConfig.cards ?? [];
  const cardBindKeys = useMemo(() => {
    const c = infoCardsConfig.cards ?? [];
    return c.map((x) => x.bindValue || x.name || "").join("|");
  }, [infoCardsConfig.cards]);

  useEffect(() => {
    const path = normalizeHrmsInfoCardsPath(apiname);
    if (!path) return;

    let cancelled = false;
    const run = async () => {
      try {
        const stats = await apiService.get<Record<string, unknown>>(path, undefined, {
          includeAuthToken: true,
          authHeaderType: "Bearer",
          errorHandling: "return-null",
        });
        if (cancelled || !stats || typeof stats !== "object") return;

        const payload = stats as Record<string, any>;
        const dataPayload =
          payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)
            ? (payload.data as Record<string, any>)
            : payload;

        const keys = (infoCardsConfig.cards ?? [])
          .map((c) => c.bindValue)
          .filter(Boolean) as string[];
        const patch: Record<string, any> = {};
        for (const k of keys) {
          if (dataPayload[k] !== undefined && dataPayload[k] !== null) {
            patch[k] = dataPayload[k];
          }
        }
        if (Object.keys(patch).length === 0) return;

        const current = store.getState().forms.formDataByScreen[screenId] || {};
        dispatch(
          setScreenFormData({
            screenId,
            data: { ...current, ...patch },
          })
        );
      } catch {
        /* ignore */
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [apiname, screenId, dispatch, cardBindKeys]);

  const stats = cards.map((card: CardConfig) => {
    const key = card.bindValue || card.name || "";
    const value =
      formData[key] !== undefined && formData[key] !== null && formData[key] !== ""
        ? formData[key]
        : card.defaultValue ?? 0;

    return {
      id: key || card.name || "card",
      label: card.name || key,
      value,
      icon: card.icon,
      accentColor: card.color || "#3B82F6",
      description: card.description || "",
    };
  });

  return <HrmStatCards stats={stats} variant="default" />;
};

export default InfoCardsWithStats;
